import { useQueryClient } from "@tanstack/react-query";
import { Table } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomDateTabs,
  CustomPopup,
  CustomTable,
  Filter,
  FilterTag,
} from "../../../../../components";

import rebateModeEnum from "../../../../../enumerations/rebateModeEnum";
import { moneyFormat } from "../../../../../plugins/numberFormat";
import { envChangeType, getCurrency } from "../../../../../plugins/utils";
import {
  filterKeyMemberBetAmountData,
  filterKeyMemberData,
} from "../../enumerations/filterKeyEnum";
import {
  filterSetupMemberBetAmountData,
  filterSetupMemberData,
} from "../../enumerations/filterSetupEnum";
import memberDataRoleEnum from "../../enumerations/memberDataRoleEnum";
import {
  useGetSubordinateMemberData,
  useGetSubordinateUserDetailData,
} from "../../hooks/subordinate.hooks";
import styles from "./style.module.scss";
const MemberData = ({ currentUser, currentSearchTime, outerTab, rebateMode = undefined }) => {
  const nowCurrency = getCurrency();
  const { t } = useTranslation();
  const cache = useQueryClient();

  const [filterDefaultValue, setFilterDefaultValue] = useState({
    [filterKeyMemberData.ACCOUNT]: "",
    [filterKeyMemberData.IS_AGENT]: null,
    [filterKeyMemberData.LEVEL]: "all",
    [filterKeyMemberData.SETTLED_START_DATE]: dayjs().format("YYYY-MM-DD 00:00:00"),
    [filterKeyMemberData.SETTLED_END_DATE]: dayjs().format("YYYY-MM-DD 23:59:59"),
  });
  const [filterValue, setFilterValue] = useState({
    [filterKeyMemberData.ACCOUNT]: "",
    [filterKeyMemberData.IS_AGENT]: null,
    [filterKeyMemberData.LEVEL]: "all",
    [filterKeyMemberData.SETTLED_START_DATE]: dayjs().format("YYYY-MM-DD 00:00:00"),
    [filterKeyMemberData.SETTLED_END_DATE]: dayjs().format("YYYY-MM-DD 23:59:59"),
  });

  const [filterDefaultBetAmountValue, setFilterDefaultBetAmountValue] = useState({
    [filterKeyMemberBetAmountData.ACCOUNT]: "",
    [filterKeyMemberBetAmountData.AGENT_ACCOUNT]: "",
    [filterKeyMemberBetAmountData.IS_AGENT]: null,
    [filterKeyMemberBetAmountData.LEVEL]: "all",
    [filterKeyMemberBetAmountData.SETTLED_START_DATE]: dayjs().format("YYYY-MM-DD 00:00:00"),
    [filterKeyMemberBetAmountData.SETTLED_END_DATE]: dayjs().format("YYYY-MM-DD 23:59:59"),
  });
  const [filterBetAmountValue, setFilterBetAmountValue] = useState({
    [filterKeyMemberBetAmountData.ACCOUNT]: "",
    [filterKeyMemberBetAmountData.AGENT_ACCOUNT]: "",
    [filterKeyMemberBetAmountData.IS_AGENT]: null,
    [filterKeyMemberBetAmountData.LEVEL]: "all",
    [filterKeyMemberBetAmountData.SETTLED_START_DATE]: dayjs().format("YYYY-MM-DD 00:00:00"),
    [filterKeyMemberBetAmountData.SETTLED_END_DATE]: dayjs().format("YYYY-MM-DD 23:59:59"),
  });

  const [selectedFilterValue, setSelectedFilterValue] = useState();
  const [selectedDefaultFilterValue, setSelectedDefaultFilterValue] = useState();

  const [localData, setLocalData] = useState([]);
  const [tableSummary, setTableSummary] = useState();
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [openDetailDrawer, setOpenDetailDrawer] = useState(false);
  const [detailTitle, setDetailTitle] = useState("");
  const [summary, setSummary] = useState({
    totalBetAmount: 0,
    totalWinLoss: 0,
  });

  const onOpenDetail = (id, account_name) => {
    setSelectedRowId(id);
    setOpenDetailDrawer(true);
    setDetailTitle(account_name);
  };

  const resetDrawer = () => {
    setDetailTitle("");
    setSelectedRowId(null);
  };
  const memberDataColumns = [
    {
      title: t("normal.account"),
      width: 230,
      dataIndex: "account",
      key: "account",
      align: "center",
    },
    {
      title: t("normal.agent.account"),
      width: 240,
      dataIndex: "agent_account",
      align: "center",
      hideInTable: rebateMode === rebateModeEnum.WINLOSE,
    },
    {
      title: t("normal.level"),
      width: 90,
      dataIndex: "agent_level",
      key: "agent_level",
      align: "center",
    },
    {
      title: t("subordinate.userBasicInfo.isAgent"),
      dataIndex: "is_agent",
      key: "is_agent",
      width: 150,
      align: "center",
      render: (text, record, index) => {
        const label = memberDataRoleEnum.find((item) => item.value === text).label;
        return <>{t(label)}</>;
      },
    },
    {
      title: t("normal.uid"),
      width: 90,
      dataIndex: "account_id",
      key: "account_id",
      align: "center",
    },
    {
      title: t("normal.registrationTime"),
      dataIndex: "register_date",
      key: "register_date",
      width: 200,
      align: "center",
      render: (text, record, index) => <>{text === null ? "-" : text}</>,
    },
    {
      title: t("subordinate.userBasicInfo.lastLoggedInTime"),
      dataIndex: "login_date",
      key: "login_date",
      width: 200,
      align: "center",
      render: (text, record, index) => <>{text === null ? "-" : text}</>,
    },
    {
      title: t("mobile.subordinate.userData.deposit", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_deposit_amount",
      key: "sum_deposit_amount",
      width: 100,
      align: "center",
      render: (text, record, index) => {
        return <>{moneyFormat(text)}</>;
      },
    },
    {
      title: t("mobile.subordinate.userData.withdraw", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      currency: t(`global.currency.${nowCurrency}`),
      dataIndex: "sum_withdrawal_amount",
      key: "sum_withdrawal_amount",
      width: 100,
      align: "center",
      render: (text, record, index) => {
        return <>{moneyFormat(text)}</>;
      },
    },
    {
      title: t("mobile.subordinate.userData.bonus", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_promotion_rebate_amount",
      key: "sum_promotion_rebate_amount",
      width: 100,
      align: "center",
      render: (text, record, index) => {
        return <>{moneyFormat(text)}</>;
      },
    },
    {
      title: t("mobile.subordinate.userData.bet", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_bet_amount",
      key: "sum_bet_amount",
      width: 120,
      align: "center",
      render: (text, record, index) => {
        return <>{moneyFormat(text)}</>;
      },
    },
    {
      title: t("mobile.subordinate.userData.winlost", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_win_loss",
      key: "sum_win_loss",
      width: 100,
      fixed: "right",
      align: "center",
      render: (text, record, index) => {
        return (
          <div
            onClick={() => onOpenDetail(record.account_id, record.account)}
            className={`${parseFloat(text) > 0 ? styles.textGreen : styles.textRed} ${
              styles.underlined
            }`}
          >
            {moneyFormat(text)}
          </div>
        );
      },
    },
  ];

  const userDetailDataColumns = [
    {
      title: t("subordinate.userData.gameCategory"),
      dataIndex: "game_category_code",
      align: "center",
      width: 120,
      render: (text, record, index) => {
        return `${t([
          `comission.platform.game.${envChangeType("REACT_APP_PRODUCTION_NAME_DEV")}.${record.game_3th_code}`,
          `comission.platform.game.${record.game_3th_code}`,
        ])}`;
      },
    },
    {
      title: t("mobile.subordinate.userData.churn", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_bet_amount",
      align: "center",
      width: 120,
      render: (text, record, index) => {
        return <>{moneyFormat(record.sum_bet_amount)}</>;
      },
    },
    {
      title: t("mobile.subordinate.userData.winlost", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_win_loss",
      align: "center",
      width: 120,
      render: (text, record, index) =>
        typeof record.sum_win_loss == "number" ? (
          <span className={`${parseFloat(text) > 0 ? styles.textGreen : styles.textRed}`}>
            {moneyFormat(record.sum_win_loss)}
          </span>
        ) : (
          <>{record.sum_win_loss}</>
        ),
    },
  ];
  const {
    data: userData,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    isError,
  } = useGetSubordinateMemberData({
    params: {
      ...selectedFilterValue,
    },
    options: {
      enabled: !!selectedFilterValue,
      getNextPageParam: (lastPage, pages) => {
        if (pages.length < lastPage.last_page) {
          return pages.length + 1;
        } else {
          return undefined;
        }
      },
    },
  });
  const {
    data: userDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useGetSubordinateUserDetailData({
    params: { ...selectedFilterValue },
    childId: selectedRowId,
    options: {
      enabled: selectedRowId !== null && selectedFilterValue !== null,
    },
  });

  const loadMore = () => {
    if (isFetchingNextPage === false && localData.length > 0) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    if (userData) {
      const page_length = userData.pages.length;
      if (page_length <= 1) {
        setLocalData([...userData.pages[0].data]);
        setTableSummary({ ...userData.pages[0] });
      } else {
        setLocalData((prevState) => {
          return [...prevState, ...userData.pages[page_length - 1].data];
        });
        setTableSummary({ ...userData.pages[page_length - 1] });
      }
    }
  }, [userData]);

  useEffect(() => {
    if (rebateMode) {
      if (rebateMode !== rebateModeEnum.WINLOSE) {
        setSelectedDefaultFilterValue(filterDefaultBetAmountValue);
        if (!!currentUser && !!currentSearchTime) {
          setSelectedFilterValue({
            ...filterBetAmountValue,
            [filterKeyMemberData.ACCOUNT]: currentUser,
            [filterKeyMemberData.SETTLED_START_DATE]: currentSearchTime.startTime,
            [filterKeyMemberData.SETTLED_END_DATE]: currentSearchTime.endTime,
          });
        } else {
          setSelectedFilterValue(filterBetAmountValue);
        }
      } else {
        setSelectedDefaultFilterValue(filterDefaultValue);
        if (!!currentUser && !!currentSearchTime) {
          setSelectedFilterValue({
            ...filterValue,
            [filterKeyMemberData.ACCOUNT]: currentUser,
            [filterKeyMemberData.SETTLED_START_DATE]: currentSearchTime.startTime,
            [filterKeyMemberData.SETTLED_END_DATE]: currentSearchTime.endTime,
          });
        } else {
          setSelectedFilterValue(filterValue);
        }
      }
    }
  }, [rebateMode, currentUser, currentSearchTime]);

  const onHandleSearch = () => {};

  useEffect(() => {
    if (userDetail) {
      let totalBetAmount = 0;
      let totalWinLoss = 0;
      userDetail.data.forEach((data) => {
        totalBetAmount += data.sum_bet_amount;
        totalWinLoss += data.sum_win_loss;
      });
      setSummary({
        totalBetAmount: totalBetAmount,
        totalWinLoss: totalWinLoss,
      });
    }
  }, [userDetail]);
  const renderSummary = () => {
    if (rebateMode !== rebateModeEnum.WINLOSE) {
      return (
        <Table.Summary fixed="top">
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
              <div className={styles.summaryCell}>{t("normal.summary")}</div>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} />
            <Table.Summary.Cell index={2} />
            <Table.Summary.Cell index={3} />
            <Table.Summary.Cell index={4} />
            <Table.Summary.Cell index={5} />
            <Table.Summary.Cell index={6} />
            <Table.Summary.Cell index={7}>
              <div className={`${styles.summaryCell} ${styles.content}`}>
                {moneyFormat(tableSummary?.total_deposit || 0)}
              </div>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={8}>
              <div className={`${styles.summaryCell} ${styles.content}`}>
                {moneyFormat(tableSummary?.total_withdrawal || 0)}
              </div>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={9}>
              <div className={`${styles.summaryCell} ${styles.content}`}>
                {moneyFormat(tableSummary?.total_promotion_rebate || 0)}
              </div>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={10}>
              <div className={`${styles.summaryCell} ${styles.content}`}>
                {moneyFormat(tableSummary?.total_bet || 0)}
              </div>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={11}>
              <div
                className={`${styles.summaryCell} ${styles.content} ${
                  !tableSummary?.total_win_loss || tableSummary?.total_win_loss <= 0
                    ? styles.redColor
                    : styles.greenColor
                }`}
              >
                {moneyFormat(tableSummary?.total_win_loss || 0)}
              </div>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      );
    }
    return (
      <Table.Summary fixed="top">
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <div className={styles.summaryCell}>{t("normal.summary")}</div>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1} />
          <Table.Summary.Cell index={2} />
          <Table.Summary.Cell index={3} />
          <Table.Summary.Cell index={4} />
          <Table.Summary.Cell index={5} />
          <Table.Summary.Cell index={6}>
            <div className={`${styles.summaryCell} ${styles.content}`}>
              {moneyFormat(tableSummary?.total_deposit || 0)}
            </div>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={7}>
            <div className={`${styles.summaryCell} ${styles.content}`}>
              {moneyFormat(tableSummary?.total_withdrawal || 0)}
            </div>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={8}>
            <div className={`${styles.summaryCell} ${styles.content}`}>
              {moneyFormat(tableSummary?.total_promotion_rebate || 0)}
            </div>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={9}>
            <div className={`${styles.summaryCell} ${styles.content}`}>
              {moneyFormat(tableSummary?.total_bet || 0)}
            </div>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={10}>
            <div
              className={`${styles.summaryCell} ${styles.content} ${
                !tableSummary?.total_sum_win_loss || tableSummary?.total_sum_win_loss <= 0
                  ? styles.redColor
                  : styles.greenColor
              }`}
            >
              {moneyFormat(tableSummary?.total_win_loss || 0)}
            </div>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <div>
      <Filter
        filterSetup={
          rebateMode === rebateModeEnum.WINLOSE
            ? filterSetupMemberData
            : filterSetupMemberBetAmountData
        }
        filterDefaultValue={selectedDefaultFilterValue}
        filterValue={selectedFilterValue}
        setFilterValue={setSelectedFilterValue}
        onReset={() => {
          //if need some aditional reset logic in the page
          console.debug("RESET FILTER");
        }}
        onSubmitSearch={onHandleSearch}
        activeTab={outerTab}
      />
      <CustomDateTabs
        filterValue={selectedFilterValue}
        setFilterValue={setSelectedFilterValue}
        startDateKey={filterKeyMemberBetAmountData.SETTLED_START_DATE}
        endDateKey={filterKeyMemberBetAmountData.SETTLED_END_DATE}
      />
      <FilterTag
        filterSetup={
          rebateMode === rebateModeEnum.WINLOSE
            ? filterSetupMemberData
            : filterSetupMemberBetAmountData
        }
        filterValue={selectedFilterValue}
        filterDefaultValue={selectedDefaultFilterValue}
        setFilterValue={setSelectedFilterValue}
        refreshSearch={onHandleSearch}
      />
      {isLoading ? (
        ""
      ) : isError ? (
        ""
      ) : (
        <CustomTable
          columns={memberDataColumns}
          dataSource={localData}
          hasMore={hasNextPage}
          loadMore={loadMore}
          showInifineScroll={true}
          summary={renderSummary}
        />
      )}
      <CustomPopup
        visible={openDetailDrawer}
        onClose={() => {
          setOpenDetailDrawer(false);
        }}
        afterClose={resetDrawer}
        title={detailTitle}
      >
        {isDetailLoading ? (
          ""
        ) : isDetailError ? (
          ""
        ) : (
          <CustomTable
            columns={userDetailDataColumns}
            dataSource={userDetail.data}
            showInifineScroll={false}
            summary={() => {
              return (
                <Table.Summary fixed="top">
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                      <div className={styles.summaryCell}>{t("normal.summary")}</div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <div className={`${styles.summaryCell} ${styles.content}`}>
                        {moneyFormat(summary.totalBetAmount || 0)}
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <div
                        className={`${styles.summaryCell} ${styles.content} ${
                          summary.totalWinLoss <= 0 ? styles.redColor : styles.greenColor
                        }`}
                      >
                        {moneyFormat(summary.totalWinLoss || 0)}
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} />
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        )}
      </CustomPopup>
    </div>
  );
};

export default MemberData;
